import { SearchIcon, XIcon } from "lucide-react";
import { useLayoutEffect, useState, useRef, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "nextjs-toploader/app";
import { useDebouncedCallback } from "use-debounce";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { useSearchTermParam } from "@/hooks/searchParams/useSearchTermParam";
import { useScopedI18n } from "@/locales/client";
import { Button } from "@/components/ui/button";
export const SearchBar = () => {
  const [searchTerm, setSearchTerm] = useSearchTermParam();
  const [value, setValue] = useState(searchTerm);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const t = useScopedI18n("mainMenu");
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    setValue(searchTerm ?? "");
  }, [searchTerm]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const debouncedSetSearchTerm = useDebouncedCallback((value: string) => {
    setSearchTerm(value);
  }, 300);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    debouncedSetSearchTerm(e.target.value);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (pathname !== "/shop") {
        router.push(`/shop?` + searchParams.toString());
      }
      setIsOpen(false);
    }
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };
  const handleClear = () => {
    setValue("");
    setSearchTerm("");
  };
  const toggleSearch = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Focus the input after the animation completes
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  };
  return <div ref={searchRef} className="relative h-10" data-sentry-component="SearchBar" data-sentry-source-file="SearchBar.tsx">
      <div className="md:hidden">
        <div className="relative w-full">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <SearchIcon className="h-4 w-4 text-muted-foreground" data-sentry-element="SearchIcon" data-sentry-source-file="SearchBar.tsx" />
          </div>
          <Input className="w-full pl-10 pr-10 text-primary" placeholder={t("searchPlaceholder")} type="text" value={value} onChange={handleChange} onKeyDown={handleKeyDown} data-sentry-element="Input" data-sentry-source-file="SearchBar.tsx" />
          {value && <Button aria-label="Clear search" className="absolute inset-y-0 right-0 flex items-center px-3" size="icon" variant="ghost" onClick={handleClear}>
              <XIcon className="h-4 w-4" />
            </Button>}
        </div>
      </div>
      <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="SearchBar.tsx">
        {isOpen && <motion.div animate={{
        width: "15rem",
        opacity: 1
      }} className="absolute right-0 top-0 hidden md:block" exit={{
        width: 0,
        opacity: 0
      }} initial={{
        width: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }}>
            <div className="relative w-full">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <SearchIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input ref={inputRef} className="pl-10 pr-10 text-primary" placeholder={t("searchPlaceholder")} type="text" value={value} onChange={handleChange} onKeyDown={handleKeyDown} />
              {value && <Button aria-label="Clear search" className="absolute inset-y-0 right-0 flex items-center px-3" size="icon" variant="ghost" onClick={handleClear}>
                  <XIcon className="h-4 w-4" />
                </Button>}
            </div>
          </motion.div>}
      </AnimatePresence>
      <Button aria-label="Search" className="hidden md:flex" size="icon" variant="ghost" onClick={toggleSearch} data-sentry-element="Button" data-sentry-source-file="SearchBar.tsx">
        <SearchIcon className="h-6 w-6" data-sentry-element="SearchIcon" data-sentry-source-file="SearchBar.tsx" />
      </Button>
    </div>;
};